import { track } from "@/features/analytics";
import DialogBase from "@components/ui/dialog-base";
import { cn } from "@lib/cn";
import googleDriveIcon from "@/assets/icons/google-drive.png";
import { IconUpload } from "@tabler/icons-react";
import { useRef } from "react";
import { useFileToStorydocStoreShallow } from "../stores";
import { CAP_MESSAGE, useImportsAtCap } from "../stores/import-registry.store";
import { trackImport } from "../utils/track-import";
import {
  IMPORT_FILE_ACCEPT,
  validateImportFile,
} from "../utils/validate-import-file";
import { RecentlyImportedList } from "./recently-imported-list";

const SOURCE_CARD_CLASS =
  "tw-flex tw-h-[144px] tw-w-[256px] tw-shrink-0 tw-flex-col tw-items-center tw-justify-center tw-gap-4 tw-rounded-2xl tw-bg-white tw-px-4 tw-py-8 tw-text-base tw-font-medium tw-text-blue tw-shadow-xs tw-outline tw-outline-1 tw-outline-offset-0 tw-outline-blue/5 tw-transition-shadow hover:tw-shadow-md focus-visible:tw-outline-2 focus-visible:tw-outline-violet-700";

const SOURCE_ICON_TILE_CLASS =
  "tw-flex tw-size-10 tw-items-center tw-justify-center tw-rounded-xl tw-bg-blue-50 tw-p-2";

export const SourceStep = ({
  onPickLocal,
  onPickDrive,
  drivePending,
}: {
  onPickLocal: (file: File) => void;
  onPickDrive: () => void;
  drivePending: boolean;
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { inlineError, inlineErrorRetry, setInlineError, isDeck } =
    useFileToStorydocStoreShallow((s) => ({
      inlineError: s.inlineError,
      inlineErrorRetry: s.inlineErrorRetry,
      setInlineError: s.setInlineError,
      isDeck: s.target === "deck",
    }));
  const atCap = useImportsAtCap() && !isDeck;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const check = validateImportFile(file);
    if (!check.ok) {
      setInlineError(check.message);
      trackImport("error", {
        source: "local",
        importJobId: null,
        stage: "select",
        code: check.reason,
      });
      return;
    }
    setInlineError(null);
    track("select pdf to storydoc file");
    trackImport("source chosen", { source: "local", importJobId: null });
    onPickLocal(file);
  };

  return (
    <DialogBase.Content className="tw-flex tw-min-h-0 tw-w-full tw-flex-1 tw-flex-col tw-p-0">
      <input
        ref={inputRef}
        type="file"
        accept={IMPORT_FILE_ACCEPT}
        className="tw-hidden"
        aria-label="Upload a file to convert to a Storydoc"
        onChange={handleChange}
      />
      {atCap ? (
        <p
          role="status"
          className="tw-mx-auto tw-max-w-[528px] tw-rounded-2xl tw-bg-blue-50 tw-px-6 tw-py-5 tw-text-center tw-text-base tw-font-medium tw-text-blue"
        >
          {CAP_MESSAGE}
        </p>
      ) : (
        <>
          <div className="tw-flex tw-justify-center tw-gap-3">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className={SOURCE_CARD_CLASS}
            >
              <span className={SOURCE_ICON_TILE_CLASS}>
                <IconUpload
                  stroke={1}
                  className="tw-size-6 !tw-fill-none tw-text-blue-500"
                />
              </span>
              Upload from your computer
            </button>
            <button
              type="button"
              onClick={onPickDrive}
              disabled={drivePending}
              aria-busy={drivePending}
              className={cn(
                SOURCE_CARD_CLASS,
                "disabled:tw-cursor-wait disabled:tw-opacity-60",
              )}
            >
              <span className={SOURCE_ICON_TILE_CLASS}>
                <img src={googleDriveIcon} alt="" className="tw-size-6" />
              </span>
              Import from Google Drive
            </button>
          </div>
          {inlineError && (
            <p
              role="alert"
              className="tw-mt-3 tw-text-center tw-text-xs tw-text-red"
            >
              {inlineError}
              {inlineErrorRetry === "drive" && (
                <>
                  {" "}
                  <button
                    type="button"
                    onClick={onPickDrive}
                    disabled={drivePending}
                    className="tw-font-medium tw-underline"
                  >
                    Try again
                  </button>
                </>
              )}
            </p>
          )}
        </>
      )}
      <RecentlyImportedList />
    </DialogBase.Content>
  );
};
